import React from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Loader } from '@modernloop/shared/helper-components';
import { CrossIcon } from '@modernloop/shared/icons';
import { reportError } from '@modernloop/shared/observability';
import { Box, Button, Divider, Paper, Stack, Theme, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import {
  PendingApproval_ModuleMemberFragment,
  usePendingApprovalApplicationStageInterviewInterviewerUpdateMutation,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ModuleMemberTrainingRole from 'src/entities/InterviewModuleMember/Statuses/ModuleMemberTrainingRole';
import { doesModuleMemberNeedsApproval, resetModuleMemberListCache } from 'src/entities/InterviewModuleMember/utils';

import InterviewLog from '../InterviewLog';
import InterviewLogInterview from '../InterviewLog/InterviewLogInterview';

import ActionButtons from './ActionButtons';
import PendingApprovalAlert from './PendingApprovalAlert';

type Fragments = {
  moduleMember: PendingApproval_ModuleMemberFragment;
};

const PendingApproval: FCWithFragments<Fragments> = ({ moduleMember }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [updateShouldCountForModule, { loading: shouldCountForModuleLoading }] =
    usePendingApprovalApplicationStageInterviewInterviewerUpdateMutation({
      refetchQueries: ['InterviewModuleMemberSidepanelQuery', 'InterviewModuleMemberModuleLogSidepanel'],
    });

  const pendingApprovalInterview = moduleMember.pendingApprovalApplicationStageInterview;
  const isPendingState = doesModuleMemberNeedsApproval(
    moduleMember.status,
    moduleMember.stats?.currentTrainingSessionStatus || undefined
  );

  // Show component if trainee is in pending state, even without a pending approval interview
  if (!pendingApprovalInterview && !isPendingState) {
    return null;
  }

  const handleUpdate = async () => {
    try {
      await updateShouldCountForModule({
        variables: {
          input: {
            applicationStageInterviewId: moduleMember?.pendingApprovalApplicationStageInterview?.id,
            interviewerId: moduleMember.employeeId,
            shouldCountForModule: false,
            interviewerRole: null,
          },
        },
        update: (cache) => {
          resetModuleMemberListCache({
            cache,
            moduleId: moduleMember.interviewModuleId,
            employeeIds: [moduleMember.employeeId],
          });
        },
      });
      enqueueSnackbar(t('hiring-dashboard-interviewers:interviewers.pendingApproval.markedDidntCountToast'), {
        variant: 'success',
      });
    } catch (err) {
      reportError(err);
      enqueueSnackbar(err.message, { variant: 'error' });
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{ width: '100%', overflow: 'hidden', borderColor: (theme: Theme) => theme.palette.warning.main }}
    >
      <Stack
        direction="column"
        width="100%"
        sx={{
          backgroundImage: (theme: Theme) =>
            `linear-gradient(180deg, ${theme.palette.warning.tint} 0%, transparent 100%)`,
        }}
      >
        <Box pt={1.5} px={1.5}>
          <PendingApprovalAlert moduleMember={moduleMember} />
        </Box>
        <Box pt={1.5} px={2.5} pb={2.5}>
          <ActionButtons moduleMember={moduleMember} disableActions={shouldCountForModuleLoading} />
        </Box>

        {/* Only show interview details section if there's a pending approval interview */}
        {pendingApprovalInterview && (
          <>
            <Divider />
            <Stack padding="12px 20px" spacing={1.5}>
              {!shouldCountForModuleLoading && (
                <Stack direction="column" spacing={1} width="100%">
                  <Stack direction="row" alignItems="center" justifyContent="space-between" p={1}>
                    <Typography color="text.secondary" variant="subtitle2">
                      {t('hiring-dashboard-interviewers:interviewers.pendingApproval.interviewBeingEvaluated')}
                    </Typography>
                    <ModuleMemberTrainingRole status={moduleMember.status} />
                  </Stack>
                  <InterviewLogInterview
                    moduleMember={moduleMember}
                    interview={pendingApprovalInterview}
                    role={moduleMember.status}
                    showNagButton
                  />
                </Stack>
              )}
              {!shouldCountForModuleLoading && (
                <Button
                  startIcon={<CrossIcon />}
                  fullWidth={false}
                  variant="contained"
                  color="secondary"
                  onClick={handleUpdate}
                  sx={{ width: 'fit-content', alignSelf: 'end' }}
                  size="small"
                >
                  {t('hiring-dashboard-interviewers:interviewers.pendingApproval.interviewDidntCount')}
                </Button>
              )}
              {shouldCountForModuleLoading && (
                <Box p={2}>
                  <Loader loading={shouldCountForModuleLoading} />
                </Box>
              )}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};

PendingApproval.fragments = {
  moduleMember: gql`
    ${InterviewLog.fragments.interviewLog}
    ${ActionButtons.fragments.moduleMember}
    ${InterviewLogInterview.fragments.interview}
    ${InterviewLogInterview.fragments.moduleMember}
    ${PendingApprovalAlert.fragments.moduleMember}
    fragment PendingApproval_moduleMember on InterviewModuleMember {
      id
      interviewModuleId
      employeeId
      status
      stats {
        currentTrainingSessionStatus
      }
      pendingApprovalApplicationStageInterview {
        id
        startAt
        ...InterviewLogInterview_interview
      }
      ...ActionButtons_moduleMember
      ...InterviewLogInterview_moduleMember
      ...PendingApprovalAlert_moduleMember
    }
  `,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UpdateDontCountMutation = gql`
  mutation PendingApprovalApplicationStageInterviewInterviewerUpdate(
    $input: ApplicationStageInterviewInterviewerUpdateInput!
  ) {
    applicationStageInterviewInterviewerUpdate(input: $input) {
      result
    }
  }
`;

export default PendingApproval;
